import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Search } from 'lucide-react';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldSearch from '@/components/FormFieldSearch';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import { REPRESENTS } from '@/utils/constant';
import { clientContactSearch } from '@/services/Network';
import { useClientBillingPreferenceGet } from '@/services/clients';
import { InvoiceType } from '@/types/accountsReceivable.types';
import CreateUpdateClientModal from '@pages/caseManagement/CreateUpdateClientModal';
import type { ContactGetAllParams, ContactResponseType } from '@/types/client.types';
import type { HIEditFormValues } from './hi.schema';

const SearchIcon = {
  Icon: Search,
  iconProps: { className: 'text-[#969696] h-5 w-5' },
};

export const HIEditClientDetails = () => {
  const { control, setValue, getValues, clearErrors } = useFormContext<HIEditFormValues>();

  const clientId = getValues('clientId');
  const [refreshKey, setRefreshKey] = useState(0);
  const [addNewContactFor, setAddNewContactFor] = useState<ContactResponseType | null>(null);

  const { data: billingPreferenceData } = useClientBillingPreferenceGet(clientId);

  useEffect(() => {
    if (billingPreferenceData?.data?.billingType === InvoiceType.PREPAID) {
      setValue('prePaid', true);
    }
  }, [billingPreferenceData]);

  const handleSameDayRush = (checked: boolean) => {
    setValue('sameDayRushDisplay', checked);
    setValue('nextDayRushDisplay', false);
    setValue('rushType', checked ? 'SAME_DAY' : 'NONE');
    setValue('lastServeDate', checked ? new Date() : null);
  };

  const handleNextDayRush = (checked: boolean) => {
    setValue('nextDayRushDisplay', checked);
    setValue('sameDayRushDisplay', false);
    setValue('rushType', checked ? 'NEXT_DAY' : 'NONE');
    if (checked) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setValue('lastServeDate', tomorrow);
    } else {
      setValue('lastServeDate', null);
    }
  };

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      {/* Read-only display row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="clientName"
          label="Client Name"
          placeholder="Client Name"
          disabled
        />
        <FormFieldComponent
          control={control}
          name="workOrderNumber"
          label="Work Order #"
          placeholder="Work Order #"
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ContactGetAllParams>
          name="contactId"
          key={refreshKey}
          placeholder="Search Client's Contact"
          label="Client's Contact"
          props={SearchIcon}
          required
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, clientId };
            return clientContactSearch(updatedParams, options, undefined, refreshKey);
          }}
          onChange={(_value, option) => {
            const contact = option as { email?: string; contactEmail?: string } | undefined;
            if (contact) {
              setValue('contactEmail', contact.email ?? contact.contactEmail ?? null);
            }
          }}
          {...(clientId && {
            addNewOption: {
              label: 'Add New Client Contact',
              onNewOptionClick: () => setAddNewContactFor('STAFF' as ContactResponseType),
            },
          })}
        />
        <FormFieldComponent
          control={control}
          name="contactEmail"
          label="Contact Email"
          placeholder="Contact Email"
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="fileNumber"
          label="File #"
          placeholder="File #"
          disabled
        />
        <FormFieldComponent
          control={control}
          name="caseNumber"
          label="Case #"
          placeholder="Case #"
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ContactGetAllParams>
          name="lawyer"
          placeholder="Search Attorney's Name"
          label="Attorney Name"
          props={SearchIcon}
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, clientId };
            return clientContactSearch(updatedParams, options, 'LAWYER' as ContactResponseType, refreshKey);
          }}
        />
        <FormFieldSelect
          name="represents"
          sortOptions
          options={REPRESENTS}
          placeholder="Select Represents"
          label="Represents"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="caseName"
          label="Case Name"
          placeholder="Case Name"
          disabled
        />
      </div>

      <div className="flex flex-row gap-6 flex-wrap">
        <FormFieldCheckbox
          control={control}
          name="sameDayRushDisplay"
          label="Same Day Rush"
          onCheckChange={handleSameDayRush}
        />
        <FormFieldCheckbox
          control={control}
          name="nextDayRushDisplay"
          label="Next Day Rush"
          onCheckChange={handleNextDayRush}
        />
      </div>

      <div className="flex flex-row gap-6 flex-wrap">
        <FormFieldCheckbox
          control={control}
          name="prePaid"
          label="Pre-paid"
        />
      </div>

      {addNewContactFor && (
        <CreateUpdateClientModal
          onClose={() => setAddNewContactFor(null)}
          onUpdate={(updatedClient) => {
            const newContact = updatedClient.contacts?.[0];
            if (newContact) {
              if (addNewContactFor === ('STAFF' as ContactResponseType)) {
                setValue('contactId', newContact.contactId ?? '');
                setValue('contactEmail', (newContact as { email?: string }).email ?? null);
              } else if (addNewContactFor === ('LAWYER' as ContactResponseType)) {
                setValue('lawyer', newContact.contactId ?? null);
              }
              clearErrors(['contactId', 'lawyer']);
            }
            setRefreshKey((prev) => prev + 1);
          }}
          operation="UPDATE_CONTACT"
          clientId={clientId}
        />
      )}
    </div>
  );
};
